import React, { useCallback, useMemo, useState } from 'react';
import { Box, Button, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DndContext, closestCenter, type DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import MiniTiptap from './MiniTiptap';

import type {
  AnalyticRubricContent,
  RubricCell,
  RubricCriterion,
  RubricPerformanceLevel,
} from '@/shared/types/rubric';
import type { StoredStandard } from '@/shared/types/customStandard';
import { useSortableReorderSensors } from '@/shared/hooks/useSortableReorderSensors';
import { reorderModifiers } from '@/shared/utils/dndModifiers';
import { StandardsSelector } from '@/shared/components/StandardsSelector';

const DEFAULT_LEVEL_LABELS = ['Beginning', 'Developing', 'Proficient', 'Advanced', 'Distinguished'];

/** Shared cell border style */
const CELL_BORDER = '1px solid';

interface Props {
  content: AnalyticRubricContent;
  canEdit: boolean;
  ownerId: string;
  onChange: (content: AnalyticRubricContent) => void;
}

function sortedByPosition<T extends { position: number }>(items: T[]): T[] {
  return [...items].sort((a, b) => a.position - b.position);
}

function nextPosition(items: { position: number }[]): number {
  const max = items.reduce((m, i) => Math.max(m, i.position), 0);
  return max + 1000;
}

function getCell(
  cells: RubricCell[],
  criterionId: string,
  levelId: string
): RubricCell | undefined {
  return cells.find((c) => c.criterionId === criterionId && c.levelId === levelId);
}

// ── Sortable criterion row ─────────────────────────────────────────────────

interface SortableCriterionRowProps {
  criterion: RubricCriterion;
  levels: RubricPerformanceLevel[];
  cells: RubricCell[];
  canEdit: boolean;
  ownerId: string;
  criteriaCount: number;
  onUpdateTitle: (id: string, title: string) => void;
  onUpdateStandards: (id: string, standards: StoredStandard[]) => void;
  onRemove: (id: string) => void;
  onUpdateCell: (criterionId: string, levelId: string, desc: string) => void;
}

const SortableCriterionRow: React.FC<SortableCriterionRowProps> = ({
  criterion,
  levels,
  cells,
  canEdit,
  ownerId,
  criteriaCount,
  onUpdateTitle,
  onUpdateStandards,
  onRemove,
  onUpdateCell,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: criterion.id,
  });

  const [showStandards, setShowStandards] = useState(false);
  const standardsCount = (criterion.standards ?? []).length;

  const style: React.CSSProperties = {
    // Translate, not Transform: dnd-kit's post-drop FLIP transform carries scaleX/scaleY
    // that visibly squash rows whose measured box changed during the drag.
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: 'grid',
        gridTemplateColumns: `240px repeat(${levels.length}, 1fr)${canEdit ? ' 44px' : ''}`,
        borderBottom: CELL_BORDER,
        borderColor: 'divider',
        '&:last-child': { borderBottom: 0 },
        // Show delete on hover
        '& .row-action': { opacity: 0, transition: 'opacity 0.15s' },
        '&:hover .row-action': { opacity: 1 },
      }}
    >
      {/* Criterion label cell */}
      <Box
        sx={{
          borderRight: CELL_BORDER,
          borderColor: 'divider',
          p: 2,
          bgcolor: 'background.paper',
          display: 'flex',
          gap: 0.5,
          alignItems: 'flex-start',
        }}
      >
        {canEdit && (
          <Box
            {...attributes}
            {...listeners}
            sx={{
              cursor: 'grab',
              color: 'text.disabled',
              mt: 0.5,
              flexShrink: 0,
              '&:hover': { color: 'text.secondary' },
              '&:active': { cursor: 'grabbing' },
            }}
          >
            <DragIndicatorIcon fontSize="small" />
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {canEdit ? (
            <>
              <TextField
                value={criterion.title}
                onChange={(e) => onUpdateTitle(criterion.id, e.target.value)}
                variant="standard"
                size="small"
                fullWidth
                inputProps={{ style: { fontWeight: 600, fontSize: '0.875rem' } }}
                sx={{ mb: 1 }}
              />
              {showStandards ? (
                <Box>
                  <StandardsSelector
                    ownerId={ownerId}
                    value={criterion.standards ?? []}
                    onChange={(standards) => onUpdateStandards(criterion.id, standards)}
                    label="Standards"
                    scopeLabel="rubric"
                  />
                  <Typography
                    variant="caption"
                    color="primary"
                    onClick={() => setShowStandards(false)}
                    sx={{ cursor: 'pointer', mt: 0.5, display: 'inline-block' }}
                  >
                    Done
                  </Typography>
                </Box>
              ) : (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  onClick={() => setShowStandards(true)}
                  sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' }, mt: 0.5 }}
                >
                  {standardsCount > 0
                    ? `${standardsCount} standard${standardsCount !== 1 ? 's' : ''} linked`
                    : '+ Link standards'}
                </Typography>
              )}
              {criteriaCount > 1 && (
                <Tooltip title="Remove criterion">
                  <IconButton
                    className="row-action"
                    size="small"
                    color="error"
                    onClick={() => onRemove(criterion.id)}
                    sx={{ mt: 0.5 }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
            </>
          ) : (
            <Typography variant="body2" fontWeight={600}>
              {criterion.title}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Performance level cells */}
      {levels.map((level, i) => {
        const cell = getCell(cells, criterion.id, level.id);
        return (
          <Box
            key={level.id}
            sx={{
              p: 1.5,
              borderRight: i < levels.length - 1 || canEdit ? CELL_BORDER : 0,
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <MiniTiptap
              content={cell?.description ?? ''}
              editable={canEdit}
              onUpdate={(html) => onUpdateCell(criterion.id, level.id, html)}
              placeholder="Describe performance…"
            />
          </Box>
        );
      })}
      {canEdit && <Box />}
    </Box>
  );
};

// ── Main grid component ───────────────────────────────────────────────────

const RubricGrid: React.FC<Props> = ({ content, canEdit, ownerId, onChange }) => {
  const levels = useMemo(
    () => sortedByPosition(content.performanceLevels),
    [content.performanceLevels]
  );
  const criteria = useMemo(() => sortedByPosition(content.criteria), [content.criteria]);

  const sensors = useSortableReorderSensors();

  // ── Cell updates ──────────────────────────────────────────────────────────

  const updateCell = useCallback(
    (criterionId: string, levelId: string, description: string) => {
      const idx = content.cells.findIndex(
        (c) => c.criterionId === criterionId && c.levelId === levelId
      );
      const newCells = [...content.cells];
      if (idx >= 0) {
        newCells[idx] = { ...newCells[idx], description };
      } else {
        newCells.push({ criterionId, levelId, description });
      }
      onChange({ ...content, cells: newCells });
    },
    [content, onChange]
  );

  // ── Criterion operations ──────────────────────────────────────────────────

  const addCriterion = useCallback(() => {
    const id = crypto.randomUUID();
    const newCriterion: RubricCriterion = {
      id,
      title: `Criterion ${criteria.length + 1}`,
      description: '',
      position: nextPosition(content.criteria),
      standards: [],
    };
    const newCells: RubricCell[] = levels.map((l) => ({
      criterionId: id,
      levelId: l.id,
      description: '',
    }));
    onChange({
      ...content,
      criteria: [...content.criteria, newCriterion],
      cells: [...content.cells, ...newCells],
    });
  }, [content, criteria.length, levels, onChange]);

  const removeCriterion = useCallback(
    (criterionId: string) => {
      onChange({
        ...content,
        criteria: content.criteria.filter((c) => c.id !== criterionId),
        cells: content.cells.filter((c) => c.criterionId !== criterionId),
      });
    },
    [content, onChange]
  );

  const updateCriterionTitle = useCallback(
    (criterionId: string, title: string) => {
      onChange({
        ...content,
        criteria: content.criteria.map((c) => (c.id === criterionId ? { ...c, title } : c)),
      });
    },
    [content, onChange]
  );

  const updateCriterionStandards = useCallback(
    (criterionId: string, standards: StoredStandard[]) => {
      onChange({
        ...content,
        criteria: content.criteria.map((c) => (c.id === criterionId ? { ...c, standards } : c)),
      });
    },
    [content, onChange]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const sorted = sortedByPosition(content.criteria);
      const fromIdx = sorted.findIndex((c) => c.id === active.id);
      const toIdx = sorted.findIndex((c) => c.id === over.id);
      if (fromIdx < 0 || toIdx < 0) return;
      const reordered = arrayMove(sorted, fromIdx, toIdx);
      const newCriteria = reordered.map((c, i) => ({ ...c, position: (i + 1) * 1000 }));
      onChange({ ...content, criteria: newCriteria });
    },
    [content, onChange]
  );

  // ── Level (column) operations ─────────────────────────────────────────────

  const addLevel = useCallback(() => {
    const id = crypto.randomUUID();
    const labelIndex = levels.length;
    const newLevel: RubricPerformanceLevel = {
      id,
      label: DEFAULT_LEVEL_LABELS[labelIndex] ?? `Level ${labelIndex + 1}`,
      position: nextPosition(content.performanceLevels),
    };
    const newCells: RubricCell[] = criteria.map((c) => ({
      criterionId: c.id,
      levelId: id,
      description: '',
    }));
    onChange({
      ...content,
      performanceLevels: [...content.performanceLevels, newLevel],
      cells: [...content.cells, ...newCells],
    });
  }, [content, criteria, levels, onChange]);

  const removeLevel = useCallback(
    (levelId: string) => {
      onChange({
        ...content,
        performanceLevels: content.performanceLevels.filter((l) => l.id !== levelId),
        cells: content.cells.filter((c) => c.levelId !== levelId),
      });
    },
    [content, onChange]
  );

  const updateLevelLabel = useCallback(
    (levelId: string, label: string) => {
      onChange({
        ...content,
        performanceLevels: content.performanceLevels.map((l) =>
          l.id === levelId ? { ...l, label } : l
        ),
      });
    },
    [content, onChange]
  );

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box>
      {/* Grid container */}
      <Box
        sx={{
          border: CELL_BORDER,
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: 'background.paper',
        }}
      >
        {/* ── Header row: levels ─────────────────────────────── */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `240px repeat(${levels.length}, 1fr)${canEdit ? ' 44px' : ''}`,
            bgcolor: (theme) => (theme.palette.mode === 'light' ? 'grey.50' : 'grey.900'),
            borderBottom: CELL_BORDER,
            borderColor: 'divider',
          }}
        >
          {/* Criteria label */}
          <Box
            sx={{
              p: 2,
              borderRight: CELL_BORDER,
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Typography
              variant="body2"
              fontWeight={700}
              color="text.secondary"
              sx={{
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                fontSize: '0.7rem',
              }}
            >
              Criteria
            </Typography>
          </Box>

          {/* Level columns */}
          {levels.map((level, i) => (
            <Box
              key={level.id}
              sx={{
                p: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                borderRight: i < levels.length - 1 || canEdit ? CELL_BORDER : 0,
                borderColor: 'divider',
                // Show delete on hover
                '& .col-action': { opacity: 0, transition: 'opacity 0.15s' },
                '&:hover .col-action': { opacity: 1 },
              }}
            >
              {canEdit ? (
                <>
                  <TextField
                    value={level.label}
                    onChange={(e) => updateLevelLabel(level.id, e.target.value)}
                    variant="standard"
                    size="small"
                    inputProps={{
                      style: {
                        textAlign: 'center',
                        fontWeight: 600,
                        fontSize: '0.8125rem',
                      },
                    }}
                    fullWidth
                    sx={{
                      '& .MuiInput-underline:before': { borderBottom: 'none' },
                      '& .MuiInput-underline:hover:before': { borderBottom: '1px solid' },
                    }}
                  />
                  {levels.length > 1 && (
                    <Tooltip title="Remove level">
                      <IconButton
                        className="col-action"
                        size="small"
                        color="error"
                        onClick={() => removeLevel(level.id)}
                        sx={{ position: 'absolute', top: 2, right: 2 }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </>
              ) : (
                <Typography variant="body2" fontWeight={600} fontSize="0.8125rem">
                  {level.label}
                </Typography>
              )}
            </Box>
          ))}

          {canEdit && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Tooltip title="Add level">
                <IconButton size="small" onClick={addLevel}>
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>

        {/* ── Body rows: criteria (drag-and-drop) ───────────── */}
        <DndContext
          sensors={canEdit ? sensors : undefined}
          modifiers={reorderModifiers}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={criteria.map((c) => c.id)} strategy={verticalListSortingStrategy}>
            {criteria.map((criterion) => (
              <SortableCriterionRow
                key={criterion.id}
                criterion={criterion}
                levels={levels}
                cells={content.cells}
                canEdit={canEdit}
                ownerId={ownerId}
                criteriaCount={criteria.length}
                onUpdateTitle={updateCriterionTitle}
                onUpdateStandards={updateCriterionStandards}
                onRemove={removeCriterion}
                onUpdateCell={updateCell}
              />
            ))}
          </SortableContext>
        </DndContext>
      </Box>

      {canEdit && (
        <Box sx={{ mt: 2 }}>
          <Button startIcon={<AddIcon />} onClick={addCriterion} variant="outlined" size="small">
            Add Criterion
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default RubricGrid;
